import React from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ChatWidget from "./components/ChatWidget";
import AppLayout from "./components/AppLayout";
import Seo from "./components/Seo";
import Home from "./pages/Home";
import Solutions from "./pages/Solutions";
import SuccessStories from "./pages/SuccessStories";
import SuccessStoryDetail from "./pages/SuccessStoryDetail";
import Contact from "./pages/Contact";
import About from "./pages/About";
import Login from "./pages/Login";
import Assessment from "./pages/Assessment";
import CompareReviews from "./pages/CompareReviews";
import VerifyEmail from "./pages/VerifyEmail";
import CompanyDashboard from "./pages/CompanyDashboard";
import Teams from "./pages/Teams";
import Surveys from "./pages/Surveys";
import AnalyticsPage from "./pages/AnalyticsPage";
import ChatbotPage from "./pages/ChatbotPage";
import Faq from "./pages/Faq";
import Legal from "./pages/Legal";
import Pricing from "./pages/Pricing";
import Settings from "./pages/Settings";
import JoinHr from "./pages/JoinHr";

function PublicLayout() {
  const { pathname } = useLocation();
  const hideFooter = pathname === "/login" || pathname === "/register" || pathname === "/join-hr";
  return (
    <>
      <Navbar />
      <main>
        <Outlet />
      </main>
      {!hideFooter && <Footer />}
      <ChatWidget />
    </>
  );
}

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
    </div>
  );
}

const APP_ONLY_PATHS = [
  "/dashboard",
  "/teams",
  "/surveys",
  "/analytics",
  "/chatbot",
  "/settings",
  "/compare",
];

function isAppOnlyPath(pathname) {
  return APP_ONLY_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const isAuthed = !!(user && user.email);

  // Do not block marketing pages on /auth/me — Googlebot otherwise indexes a spinner.
  if (loading && isAppOnlyPath(pathname)) {
    return <FullScreenLoader />;
  }

  return (
    <Routes>
      {/* Standalone routes — accessible in any auth state */}
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/join-hr" element={<JoinHr />} />
      <Route path="/assessment/:reviewId" element={<Assessment />} />
      <Route
        path="/login"
        element={isAuthed ? <Navigate to="/dashboard" replace /> : <Login />}
      />
      <Route
        path="/register"
        element={isAuthed ? <Navigate to="/dashboard" replace /> : <Login />}
      />

      {isAuthed ? (
        <Route element={<AppLayout />}>
          <Route path="/dashboard" element={<CompanyDashboard />} />
          <Route path="/teams" element={<Teams />} />
          <Route path="/surveys" element={<Surveys />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/chatbot" element={<ChatbotPage />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/pricing" element={<Pricing inApp />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/compare" element={<CompareReviews />} />
        </Route>
      ) : null}

      <Route element={<PublicLayout />}>
        <Route
          path="/"
          element={isAuthed ? <Navigate to="/dashboard" replace /> : <Home />}
        />
        <Route path="/solutions" element={<Solutions />} />
        <Route path="/success-stories" element={<SuccessStories />} />
        <Route path="/success-stories/:slug" element={<SuccessStoryDetail />} />
        <Route path="/case-studies" element={<Navigate to="/success-stories" replace />} />
        <Route path="/about" element={<About />} />
        {!isAuthed && <Route path="/pricing" element={<Pricing />} />}
        <Route path="/contact" element={<Contact />} />
        {!isAuthed && <Route path="/faq" element={<Faq offsetTop />} />}
        <Route path="/terms" element={<Legal type="terms" />} />
        <Route path="/privacy" element={<Legal type="privacy" />} />
        <Route path="/gdpr" element={<Legal type="gdpr" />} />
      </Route>

      <Route
        path="*"
        element={<Navigate to={isAuthed ? "/dashboard" : "/"} replace />}
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <Seo />
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
